<template>
	<the-header></the-header>
	<main class="flex-1 pt-4 bg-background-gray">
		<router-view v-slot="{ Component }">
			<transition name="router" mode="out-in">
				<component :key="new Date().toString()" :is="Component"></component>
			</transition>
		</router-view>
	</main>
	<the-footer></the-footer>
	<back-to-top-button></back-to-top-button>
	<lightbox-component></lightbox-component>
</template>

<script setup>
import { onBeforeUnmount, onMounted } from 'vue'
import { useHead } from '@unhead/vue'
import { useState } from 'vue-gtag-next'
import { useAppStore } from '@/stores/appStore.js'
import { useResponsiveTables } from '@/composables/useResponsiveTables'
import { useCookieConsent } from '@/composables/useCookieConsent'
import cookieConsentConfig from '@/config/cookieConsent'

import TheHeader from '@/components/layout/header/TheHeader.vue'
import TheFooter from '@/components/layout/TheFooter.vue'
import BackToTopButton from '@/components/BackToTopButton.vue'
import LightboxComponent from '@/components/layout/LightboxComponent.vue'

const appStore = useAppStore()

useResponsiveTables()

const CookieConsent = useCookieConsent()

useHead({
  title: import.meta.env.VITE_SITE_TITLE ?? '',
  meta: [
    // Basic
    { name: 'description', content: 'Explore Cardiologia Hungarica, the official journal of the Hungarian Society of Cardiology, featuring peer-reviewed research and articles in cardiology since 1972.' },

    // Open Graph
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: 'Cardiologia Hungarica - Official Journal of the Hungarian Society of Cardiology' },
    { property: 'og:description', content: 'Cardiologia Hungarica is a peer-reviewed scientific journal dedicated to publishing the latest research and articles in cardiology. Available in print and online.' },
    { property: 'og:site_name', content: 'Cardiologia Hungarica' },
    { property: 'og:url', content: window.location.href },
    { property: 'og:image', content: window.location.origin + '/assets/ch_og.png' },
    { property: 'og:locale', content: 'en_US' },
  ]
})

function showLightbox(event) {
  if (event.target.tagName === 'IMG' && event.target.closest('.lightbox-enabled-container')) {
    appStore.setLightboxImages(event.target.src)
    appStore.toggleLightbox(true)
  }
}

onMounted(() => {
  // Register the lightbox related click listener
  document.addEventListener('click', showLightbox)

  // Google Tag integration with Cookie Consent
  cookieConsentConfig.onConsent = () => {
    if (CookieConsent.acceptedCategory('analytics')) {
      const { property } = useState()

      property.value = {
        id: import.meta.env.VITE_GA_MEASUREMENT_ID
      }
    }
  }

  // Google Tag integration with Cookie Consent
  cookieConsentConfig.onChange = ({ changedCategories }) => {
    if (changedCategories.includes('analytics')) {
      if (CookieConsent.acceptedCategory('analytics')) {
        const { property } = useState()

        property.value = {
          id: import.meta.env.VITE_GA_MEASUREMENT_ID
        }
      }
    }
  }

  CookieConsent.run(cookieConsentConfig)
})

onBeforeUnmount(() => {
  // Remove the lightbox related click listener
  document.removeEventListener('click', showLightbox)
})
</script>

<style scoped>
.router-enter-active,
.router-leave-active {
  transition: all 200ms ease-out;
}

.router-enter-to {
  opacity: 1;
}

.router-enter-from {
  opacity: 0
}

.router-leave-to {
  opacity: 0;
}

.router-leave-from {
  opacity: 1;
}
</style>
